import { faXmark } from "@fortawesome/pro-regular-svg-icons";
import { faChevronDown } from "@fortawesome/pro-solid-svg-icons/faChevronDown";
import {
  Stack,
  Select,
  FormHelperText,
  SelectProps as MuiProps,
  InputLabelProps as MuiInputLabelProps,
  FormHelperTextProps as MuiFormHelperTextProps,
  InputProps as MuiInputProps,
  IconButton,
  SelectChangeEvent,
} from "@mui/material";
import { ReactElement, ReactNode, Ref, useCallback, useRef, useState } from "react";
import { FaIcon } from "../../DataDisplay/Icon/FaIcon";
import { FormField } from "../FormField/FormField";

export type SelectFieldProps = Omit<
  MuiProps,
  "name" | "label" | "helperText" | "error" | "onChange"
> & {
  name: string;
  label?: string;
  onChange: (v: string | null) => void;
  helperText?: ReactNode;
  cornerHelperText?: ReactNode;
  leftCornerElement?: ReactNode;
  tooltip?: ReactNode;
  readOnly?: boolean;
  clearable?: boolean;
  error?: boolean | string;
  controlRef?: Ref<unknown>;
  InputProps?: MuiInputProps;
  InputLabelProps?: MuiInputLabelProps;
  FormHelperTextProps?: MuiFormHelperTextProps;
  containerId?: string;
};

/**
 * @deprecated: Use `SrSelect` (@shuttlerock/mui-components) or `SrSelectField` (@shuttlerock/react-hook-forms) instead.
 */
export const SelectField = ({
  id: inId,
  containerId,
  autoWidth,
  autoFocus,
  clearable,
  children,
  displayEmpty,
  defaultValue,
  fullWidth = true,
  multiline,
  value,
  name,
  label,
  size = "small",
  variant = "outlined",
  error,
  helperText,
  cornerHelperText,
  leftCornerElement,
  tooltip,
  controlRef,
  readOnly,
  required,
  disabled,
  onBlur,
  onChange,
  onFocus,
  renderValue,
  inputProps,
  InputProps,
  FormHelperTextProps,
  InputLabelProps,
  MenuProps,
}: SelectFieldProps): ReactElement => {
  const [isOpen, setIsOpen] = useState(false);
  const selectRef = useRef<HTMLElement | null>(undefined);

  const id = inId || name;
  const helperTextId = helperText && id ? `${id}-helper-text` : undefined;

  const handleOpen = () => {
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const ArrowDropdownIcon = useCallback(
    () =>
      readOnly ? null : (
        <IconButton
          color="inherit"
          onClick={handleOpen}
          disabled={disabled}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            position: "absolute",
            fontSize: "12px",
            right: "9px",
            transform: isOpen ? "rotate(180deg)" : "rotate(0)",
          }}
        >
          <FaIcon
            icon={faChevronDown}
            sx={(theme) => ({
              color: theme.vars.palette.grey.A500,
              [theme.getColorSchemeSelector("dark")]: {
                color: theme.vars.palette.grey.A700,
              },
            })}
          />
        </IconButton>
      ),
    [disabled, readOnly, isOpen],
  );

  const showClearButton =
    !readOnly && clearable && value !== undefined && value !== null && value !== "";

  const handleClear = () => {
    if (onChange) {
      onChange(null);
    }
  };

  const handleChange = (e: SelectChangeEvent<unknown>) => {
    if (onChange) {
      onChange(e.target.value as string | null);
    }
  };

  return (
    <FormField
      name={name}
      id={id}
      containerId={containerId}
      label={label}
      cornerHelperText={cornerHelperText}
      tooltip={tooltip}
      required={required}
      disabled={disabled}
      error={error}
      fullWidth={fullWidth}
      controlRef={controlRef}
      FormHelperTextProps={{ id: helperTextId, ...FormHelperTextProps }}
      FormLabelProps={{
        ...InputLabelProps,
        sx: {
          ...InputLabelProps?.sx,
          color: "text.primary",
          "&.Mui-focused:not(.Mui-error)": {
            color: "text.primary",
          },
        },
      }}
    >
      <Select
        ref={selectRef}
        aria-describedby={helperTextId}
        // eslint-disable-next-line jsx-a11y/no-autofocus -- support controlled focus handoff when select opens from modals or inline rename flows
        autoFocus={autoFocus}
        fullWidth={fullWidth}
        multiline={multiline}
        renderValue={renderValue}
        displayEmpty={displayEmpty}
        onOpen={handleOpen}
        onClose={handleClose}
        open={isOpen}
        size={size}
        variant={variant}
        MenuProps={{
          ...MenuProps,
          PaperProps: {
            sx: {
              maxWidth: autoWidth ? selectRef.current?.clientWidth : undefined,
            },
          },
        }}
        endAdornment={
          showClearButton ? (
            <IconButton
              color="inherit"
              size="large"
              sx={{
                width: "30px",
                height: "30px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                visibility: "hidden",
                fontSize: "12px",
                position: "absolute",
                right: "35px",
              }}
              onClick={handleClear}
            >
              <FaIcon icon={faXmark} color="secondary" />
            </IconButton>
          ) : null
        }
        IconComponent={ArrowDropdownIcon}
        inputProps={{
          "data-testid": `${id}-input`,
          ...inputProps,
        }}
        name={name}
        defaultValue={defaultValue}
        value={value || ""}
        id={id}
        readOnly={readOnly}
        onBlur={!readOnly ? onBlur : undefined}
        onChange={!readOnly ? handleChange : undefined}
        onFocus={!readOnly ? onFocus : undefined}
        sx={{
          ...InputProps?.sx,
          pointerEvents: readOnly ? "none" : undefined,
          pr: 0,
          "&:hover .MuiIconButton-root": {
            visibility: "visible",
          },
        }}
      >
        {children}
      </Select>
      <Stack direction="row" justifyContent="space-between" gap={1} mt={leftCornerElement ? 2 : 0}>
        {leftCornerElement}
        {helperText ? (
          <FormHelperText
            component="div"
            id={helperTextId}
            {...FormHelperTextProps}
            error={false}
            sx={{ flex: 1 }}
          >
            {helperText}
          </FormHelperText>
        ) : null}
      </Stack>
    </FormField>
  );
};
